import {
  CaptureMethod,
  Piece,
  PieceAnnotation,
  PieceAsset,
  PieceAssetSize,
  PieceAssetType,
  PieceSummary,
  PieceWebSocket,
  Status,
} from '@apiContract';
import { CaptureOptions } from '@rdTypes/capture';
import assign from 'lodash/fp/assign';
import assoc from 'lodash/fp/assoc';
import compose from 'lodash/fp/compose';
import isArray from 'lodash/fp/isArray';
import path from 'lodash/fp/path';
import pick from 'lodash/fp/pick';
import pickBy from 'lodash/fp/pickBy';
import remove from 'lodash/fp/remove';
import unionBy from 'lodash/fp/unionBy';
import unset from 'lodash/fp/unset';
import { v4 } from 'uuid';

import { CaptureId, DrawerTypes, MapCapturePiece, MapDetectedRecipient, PieceStatus, Steps } from '../types';

export enum ActionTypes {
  INIT_FILE = 'INIT_FILE',
  SET_CURSOR = 'SET_CURSOR',
  SET_CAPTURE_STATE = 'SET_CAPTURE_STATE',
  REMOVE_FILE = 'REMOVE_FILE',
  SET_RECEIVE_SESSION = 'SET_RECEIVE_SESSION',
  SET_FILE_OPTION = 'SET_FILE_OPTION',
  UPDATE_FILE_ID = 'UPDATE_FILE_ID',
  SET_DRAWER = 'SET_DRAWER',
  SET_CROP_RETURN_PATH = 'SET_CROP_RETURN_PATH',
  SET_UPLOAD_ERROR = 'SET_UPLOAD_ERROR',
  UPDATE_RECEIVE_CONFIG = 'UPDATE_RECEIVE_CONFIG',
  SET_IS_LOADING_STORAGE_GROUP = 'SET_IS_LOADING_STORAGE_GROUP',
  SET_PIECE_ASSETS = 'SET_PIECE_ASSETS',
  ADD_PIECE_LOAD_LIST = 'ADD_PIECE_LOAD_LIST',
  SET_PIECE_ANNOTATIONS = 'SET_PIECE_ANNOTATIONS',
  MERGE_PIECE_ANNOTATIONS = 'MERGE_PIECE_ANNOTATIONS',
  CREATE_PIECES_FROM_DRAFTS = 'CREATE_PIECES_FROM_DRAFTS',
  REMOVE_DRAFTS = 'REMOVE_DRAFTS',
  PROCESS_WS_MESSAGE = 'PROCESS_WS_MESSAGE',
  BARCODE_DETECTED = 'PROCESS_deWS_MESSAGE',
  BARCODE_ADDED = 'BARCODE_ADDED',
  PIECE_DELETED = 'PIECE_DELETED',
}

export type Store = {
  files: MapCapturePiece;
  captureState: Steps;
  cursor: CaptureId | null;
  drawer: DrawerTypes | null;
  receiveSession: string | null;
  cropReturnPath: Steps | null;
  uploadError: boolean;
  detectedRecipients: MapDetectedRecipient;
  options: CaptureOptions;
  isLoadingStorageGroup: false;
  loadPercent: number;
};

const getCaptureState = (options: CaptureOptions, isNew: boolean) => {
  switch (true) {
    case !isNew:
      return Steps.Loading;

    case !!options.captureMethod && !!options.pieceType && !!options.workflow && !!options.storageGroupData:
      return options.captureMethod === CaptureMethod.Single ? Steps.Photo : Steps.Images;

    default:
      return Steps.ReceiveConfig;
  }
};

export const initStore = (options: CaptureOptions, isNew = false): Store => {
  return {
    files: {},
    captureState: getCaptureState(options, isNew),
    cursor: null,
    drawer: null,
    receiveSession: null,
    cropReturnPath: null,
    uploadError: false,
    detectedRecipients: {},
    options,
    isLoadingStorageGroup: false,
    loadPercent: 0,
  };
};

type InitFileAction = {
  type: ActionTypes.INIT_FILE;
  payload: {
    id?: string;
    status?: PieceStatus;
    photo: File | string;
  };
};

type SetCursorAction = {
  type: ActionTypes.SET_CURSOR;
  payload: CaptureId | null;
};

type SetCaptureStateAction = {
  type: ActionTypes.SET_CAPTURE_STATE;
  payload: Steps;
};

type RemoveFileAction = {
  type: ActionTypes.REMOVE_FILE;
  payload: CaptureId;
};

type SetReceiveSessionAction = {
  type: ActionTypes.SET_RECEIVE_SESSION;
  payload: string;
};

export type FileOptionPayload = {
  id: CaptureId | CaptureId[];
  option: string | string[];
  value: any;
  cb?: (a: Store) => void;
};

type SetFileOption = {
  type: ActionTypes.SET_FILE_OPTION;
  payload: FileOptionPayload;
};

type UpdateFileId = {
  type: ActionTypes.UPDATE_FILE_ID;
  payload: CaptureId;
};

type SetDrawerType = {
  type: ActionTypes.SET_DRAWER;
  payload: DrawerTypes | null;
};

type SetCropReturnPath = {
  type: ActionTypes.SET_CROP_RETURN_PATH;
  payload: Steps;
};

type SetUploadError = {
  type: ActionTypes.SET_UPLOAD_ERROR;
  payload: boolean;
};

type UpdateReceiveConfig = {
  type: ActionTypes.UPDATE_RECEIVE_CONFIG;
  payload: Partial<CaptureOptions>;
};

type SetIsLoadingStorageGroup = {
  type: ActionTypes.SET_IS_LOADING_STORAGE_GROUP;
  payload: boolean;
};

type SetPieceAssets = {
  type: ActionTypes.SET_PIECE_ASSETS;
  payload: {
    id: CaptureId;
    value: Array<PieceAsset>;
  };
};

type AddPieceLoadList = {
  type: ActionTypes.ADD_PIECE_LOAD_LIST;
  payload: Piece[];
};

type SetUpBarcodes = {
  type: ActionTypes.SET_PIECE_ANNOTATIONS | ActionTypes.MERGE_PIECE_ANNOTATIONS;
  payload: PieceAnnotation[];
};

type SubmitDrafts = {
  type: ActionTypes.CREATE_PIECES_FROM_DRAFTS;
  payload: number[];
};

type RemoveDrafts = {
  type: ActionTypes.REMOVE_DRAFTS;
  payload: null;
};

type ProcessWSMessage = {
  type: ActionTypes.PROCESS_WS_MESSAGE;
  payload: PieceWebSocket[];
};

type BarcodeDetected = {
  type: ActionTypes.BARCODE_DETECTED;
  payload: PieceWebSocket[];
};

type BarcodeAdded = {
  type: ActionTypes.BARCODE_ADDED;
  payload: PieceWebSocket[];
};

type PieceDeleted = {
  type: ActionTypes.PIECE_DELETED;
  payload: PieceWebSocket[];
};

export type Actions =
  | UpdateFileId
  | InitFileAction
  | SetCursorAction
  | SetCaptureStateAction
  | RemoveFileAction
  | SetReceiveSessionAction
  | SetFileOption
  | SetDrawerType
  | SetCropReturnPath
  | SetUploadError
  | UpdateReceiveConfig
  | SetIsLoadingStorageGroup
  | SetPieceAssets
  | AddPieceLoadList
  | SetUpBarcodes
  | SubmitDrafts
  | RemoveDrafts
  | ProcessWSMessage
  | BarcodeDetected
  | BarcodeAdded
  | PieceDeleted;

const setFileOptionValue = (state: Store, { id, option, value }: FileOptionPayload): Store => {
  return assoc(['files', isArray(id) ? id[0] : id, ...(isArray(option) ? option : [option])], value)(state);
};

const updatePiece = (state: Store, id: number, piece?: Piece, pieceSummary?: PieceSummary) => {
  if (piece && state.files[piece.id]) {
    return assoc(['files', piece.id, 'piece'], piece, state);
  }
  if (pieceSummary && state.files[id]?.piece) {
    return assoc(['files', id, 'piece', 'pieceSummary'], pieceSummary, state);
  }
  return state;
};

const mergeAnnotations = (
  state: Store,
  annotations?: PieceAnnotation[] | null,
  pieceId?: number,
  isBarcode?: boolean,
) => {
  if (pieceId && !state.files[pieceId]) return state;
  const nextState = isBarcode && pieceId ? assoc(['files', pieceId, 'barcodeDetected'], true, state) : state;
  if (annotations?.length) {
    return assoc(
      'files',
      {
        ...nextState.files,
        ...annotations.reduce((acc, item) => {
          if (!nextState.files[item.pieceId]) return acc;
          const nextAnnotations = unionBy(
            'id',
            [item],
            [...(nextState.files[item.pieceId].pieceAnnotations || []), ...(acc[item.pieceId]?.pieceAnnotations || [])],
          );
          acc[item.pieceId] = {
            ...nextState.files[item.pieceId],
            pieceAnnotations: nextAnnotations,
          };
          if (item.pieceSummary) {
            if (acc[item.pieceId].piece?.pieceSummary) {
              const values = pick(['trackingNumberStatus'], item.pieceSummary);
              const mergedSummary = assign((acc[item.pieceId].piece as Piece).pieceSummary, values);
              return assoc([item.pieceId, 'piece', 'pieceSummary'], mergedSummary, acc);
            }
            return assoc([item.pieceId, 'piece', 'pieceSummary'], item.pieceSummary, acc);
          }
          return acc;
        }, {} as MapCapturePiece),
      },
      nextState,
    );
  }
  return nextState;
};

const processWSMessage = (state: Store, payload: PieceWebSocket[], isBarcode?: boolean) =>
  payload.reduce((acc, item) => {
    const nextState = updatePiece(acc, item.pieceId, item.piece, item.pieceSummary);
    return mergeAnnotations(nextState, item.annotations, item.pieceId, isBarcode);
  }, state);

export const reducer = (state: Store, { type, payload }: Actions): Store => {
  switch (type) {
    case ActionTypes.INIT_FILE: {
      const id = payload.id || v4();
      return compose(
        assoc(['files', id], {
          id,
          status: 'created',
          pieceDetection: null,
          selectedPieceDetection: null, // still need?
          selectedUserDrawer: false,
          isUpdating: false,
          piece: null,
          assignUserError: false,
          isAutoCroppedCompleted: false,
          pieceAnnotations: null,
          ...payload,
        }),
        assoc(['cursor'], id),
      )(state);
    }

    case ActionTypes.SET_CURSOR: {
      return assoc(['cursor'], payload, state);
    }

    case ActionTypes.SET_CAPTURE_STATE:
      return assoc(['captureState'], payload, state);

    case ActionTypes.REMOVE_FILE:
      return unset(['files', payload], state);

    case ActionTypes.SET_RECEIVE_SESSION:
      return assoc(['receiveSession'], payload, state);

    case ActionTypes.SET_FILE_OPTION: {
      const { id, cb } = payload;
      let store;
      if (isArray(id)) {
        store = id.reduce((acc, nextId) => setFileOptionValue(acc, { ...payload, id: nextId }), state);
      } else {
        store = setFileOptionValue(state, payload);
      }
      if (cb) cb(store);
      return store;
    }

    case ActionTypes.UPDATE_FILE_ID: {
      if (!state.files[payload] || !state.files[payload].piece) return state;
      const nextState = { ...state };
      // it's impossible to be null here but typescript doesn't like it
      const nextId = state?.files?.[payload]?.piece?.id || 0;
      const nextCursor = nextState.cursor === payload ? nextId : nextState.cursor;
      return compose(
        assoc(['cursor'], nextCursor),
        unset(['files', payload]),
        assoc(['files', nextId, 'id'], nextId),
        assoc(['files', nextId], state.files[payload]),
      )(nextState);
    }

    case ActionTypes.SET_DRAWER:
      return assoc(['drawer'], payload, state);

    case ActionTypes.SET_CROP_RETURN_PATH:
      return assoc(['cropReturnPath'], payload, state);

    case ActionTypes.SET_UPLOAD_ERROR:
      return assoc(['uploadError'], payload, state);

    case ActionTypes.UPDATE_RECEIVE_CONFIG: {
      return assoc('options', { ...state.options, ...payload }, state);
    }

    case ActionTypes.SET_IS_LOADING_STORAGE_GROUP: {
      return assoc('isLoadingStorageGroup', payload, state);
    }

    case ActionTypes.SET_PIECE_ASSETS: {
      const { id, value } = payload;

      if (!state.files[id]) return state;

      if (value.length && value[0].pieceAssetType === PieceAssetType.Cropped) {
        const data = compose(
          remove(
            (n: PieceAsset) => n.pieceAssetType === PieceAssetType.Cropped && n.pieceAssetSize !== PieceAssetSize.Large,
          ),
          path(['files', id, 'piece', 'pieceAssets']),
        )(state);

        return assoc(['files', id, 'piece', 'pieceAssets'], [...data, ...value], state);
      }

      return assoc(
        ['files', id, 'piece', 'pieceAssets'],
        [...(state.files[id].piece?.pieceAssets || []), ...value],
        state,
      );
    }

    case ActionTypes.ADD_PIECE_LOAD_LIST: {
      return assoc(
        'files',
        {
          ...state.files,
          ...payload.reduce((acc, item) => {
            acc[item.id] = {
              id: item.id,
              status: 'loadedFromServer',
              isUpdating: false,
              piece: item,
              assignUserError: false,
              isAutoCroppedCompleted: false,
              pieceAnnotations: null,
            };
            return acc;
          }, {} as MapCapturePiece),
        },
        state,
      );
    }

    case ActionTypes.SET_PIECE_ANNOTATIONS: {
      return assoc(
        'files',
        {
          ...state.files,
          ...payload.reduce((acc, item) => {
            acc[item.pieceId] = {
              ...state.files[item.pieceId],
              pieceAnnotations: unionBy('id', acc[item.pieceId]?.pieceAnnotations || [], [item]),
            };
            return acc;
          }, {} as MapCapturePiece),
        },
        state,
      );
    }

    case ActionTypes.MERGE_PIECE_ANNOTATIONS:
      return mergeAnnotations(state, payload);

    case ActionTypes.CREATE_PIECES_FROM_DRAFTS: {
      const idSet = new Set(payload);

      return assoc(
        'files',
        Object.keys(state.files).reduce((acc, fileId) => {
          const file = state.files[fileId];
          const numericId = Number(fileId);

          if (file.piece?.status === Status.Draft && idSet.has(numericId)) {
            acc[fileId] = {
              ...file,
              piece: {
                ...file.piece,
                status: Status.Active,
              },
            };
          } else {
            acc[fileId] = file;
          }
          return acc;
        }, {} as typeof state.files),
        state,
      );
    }

    case ActionTypes.REMOVE_DRAFTS: {
      return assoc(
        'files',
        pickBy((file) => file.piece?.status !== Status.Draft, state.files),
        state,
      );
    }

    case ActionTypes.PROCESS_WS_MESSAGE:
      return processWSMessage(state, payload);

    case ActionTypes.BARCODE_DETECTED:
    case ActionTypes.BARCODE_ADDED:
    case ActionTypes.PIECE_DELETED:
      return processWSMessage(state, payload, true);

    default:
      return state;
  }
};
